import { type FC, type ReactNode, useId } from "react";
import { type FieldError, get, useFormState } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";

export type FormFieldControlProps = {
  id: string;
  "aria-invalid": boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
};

type FormFieldProps = {
  children: (controlProps: FormFieldControlProps) => ReactNode;
  name: string;
  label: string;
  helper?: string;
  id?: string;
  size?: "s" | "m";
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
};

/**
 * Label + helper/error wrapper for a form control. Must sit inside a `FormProvider`.
 * Reads RHF errors for `name` and passes a11y props to the control via a render-prop child:
 *
 * ```tsx
 * <FormField name="email" label="Email" helper="Helper text here" required>
 *   {(controlProps) => (
 *     <TextField {...register("email", { required: t("error.field_required") })} {...controlProps} />
 *   )}
 * </FormField>
 * ```
 *
 * Works with TextField, TextArea, and Listbox.
 * TODO: add support for other form components.
 */
export const FormField: FC<FormFieldProps> = ({
  children,
  name,
  label,
  helper,
  id: idProp,
  size = "s",
  className,
  disabled,
  readOnly,
  required = false,
}) => {
  const generatedId = useId();
  const id = idProp ?? generatedId;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;

  const { errors } = useFormState({ name });
  const error = get(errors, name) as FieldError | undefined;
  const errorMessage = typeof error?.message === "string" ? error.message : undefined;
  const invalid = !!error;
  const showError = invalid && !disabled && !readOnly && !!errorMessage;
  const describedBy = showError ? errorId : helper ? helperId : undefined;

  const labelTextSize = size === "s" ? "body-s" : "body-m";
  const descriptionTextSize = size === "s" ? "body-xs" : "body-s";
  const descriptionColor = disabled ? "text-black-tint-70" : "text-black-tint-55";

  return (
    <div className={twMerge("flex flex-col", className)}>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label
          className={twMerge(disabled ? "text-black-tint-70" : "text-black-tint-20", labelTextSize)}
          htmlFor={id}
        >
          {label}
        </label>
        {!required && (
          <div className={twMerge(descriptionColor, descriptionTextSize)}>
            {t("common.optional")}
          </div>
        )}
      </div>
      {children({
        id,
        disabled,
        readOnly,
        "aria-invalid": invalid,
        "aria-required": required,
        "aria-describedby": describedBy,
      })}
      {showError ? (
        <div
          className={twMerge("mt-1 min-w-0 break-words text-red", descriptionTextSize)}
          id={errorId}
        >
          {errorMessage}
        </div>
      ) : helper ? (
        <div className={twMerge("mt-1", descriptionColor, descriptionTextSize)} id={helperId}>
          {helper}
        </div>
      ) : null}
    </div>
  );
};
